import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const EQUIV_OPTS = ['IDENTICAL', 'SUBSUMES', 'PARTIAL', 'RELATED'];
const EQUIV_BADGE = {
  IDENTICAL: { bg: '#dcfce7', color: '#166534' },
  SUBSUMES:  { bg: '#dbeafe', color: '#1e40af' },
  PARTIAL:   { bg: '#fef9c3', color: '#854d0e' },
  RELATED:   { bg: '#f3e8ff', color: '#6b21a8' },
};

export default function MappingEditor() {
  const { t } = useTranslation();
  const [mappings, setMappings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [form, setForm]           = useState({ sourceControlId: '', targetControlId: '', equivalence: 'IDENTICAL' });
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/compliance/admin/mappings', { headers: authHeaders() });
      setMappings(res.data || []);
    } catch {
      setMsg(t('MappingEditor.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.sourceControlId || !form.targetControlId) { setMsg(t('MappingEditor.bothControlIdsRequired')); return; }
    setSaving(true);
    setMsg('');
    try {
      const res = await axiosInstance.post('/compliance/admin/mappings', {
        sourceControlId: parseInt(form.sourceControlId),
        targetControlId: parseInt(form.targetControlId),
        equivalence: form.equivalence,
      }, { headers: authHeaders() });
      setMappings(p => [res.data, ...p]);
      setForm({ sourceControlId: '', targetControlId: '', equivalence: 'IDENTICAL' });
      setMsg(t('MappingEditor.mappingCreated'));
    } catch (e) {
      setMsg(e.response?.data?.message || t('MappingEditor.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(t('MappingEditor.confirmDelete'))) return;
    try {
      await axiosInstance.delete(`/compliance/admin/mappings/${id}`, { headers: authHeaders() });
      setMappings(p => p.filter(m => m.id !== id));
      setMsg(t('MappingEditor.mappingDeleted'));
    } catch { setMsg(t('MappingEditor.deleteFailed')); }
  };

  const s = {
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20 },
    inp:  { padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 },
    btn:  (c = '#6366f1', sm) => ({
            padding: sm ? '4px 10px' : '7px 14px', background: c, color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: sm ? 11 : 13, fontWeight: 600 }),
    msg:  { padding: '8px 14px', borderRadius: 6, fontSize: 13, marginBottom: 12, background: '#dbeafe', color: '#1e40af' },
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 900 }}>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('MappingEditor.title')}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
        {t('MappingEditor.subtitle')}
      </div>

      {msg && <div style={s.msg}>{msg}</div>}

      {/* Create form */}
      <div style={s.card}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>{t('MappingEditor.newMapping')}</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MappingEditor.sourceControlId')}</div>
            <input type="number" value={form.sourceControlId} onChange={e => setForm(p => ({ ...p, sourceControlId: e.target.value }))}
              placeholder={t('MappingEditor.sourceControlIdPlaceholder')} style={{ ...s.inp, width: 130 }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MappingEditor.targetControlId')}</div>
            <input type="number" value={form.targetControlId} onChange={e => setForm(p => ({ ...p, targetControlId: e.target.value }))}
              placeholder={t('MappingEditor.targetControlIdPlaceholder')} style={{ ...s.inp, width: 130 }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('MappingEditor.equivalence')}</div>
            <select value={form.equivalence} onChange={e => setForm(p => ({ ...p, equivalence: e.target.value }))}
              style={{ ...s.inp, minWidth: 140 }}>
              {EQUIV_OPTS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <button onClick={create} disabled={saving} style={s.btn()}>
            {saving ? t('MappingEditor.creating') : t('MappingEditor.createMapping')}
          </button>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
          {t('MappingEditor.propagationHint')}
        </div>
      </div>

      {/* Mappings table */}
      <div style={s.card}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>{t('MappingEditor.existingMappings', { count: mappings.length })}</div>
        {mappings.length === 0 ? (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: 30 }}>{t('MappingEditor.noMappings')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  { key: 'sourceControl', label: t('MappingEditor.colSourceControl') },
                  { key: 'targetControl', label: t('MappingEditor.colTargetControl') },
                  { key: 'equivalence', label: t('MappingEditor.colEquivalence') },
                  { key: 'created', label: t('MappingEditor.colCreated') },
                  { key: 'actions', label: '' },
                ].map(h => (
                  <th key={h.key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => {
                const b = EQUIV_BADGE[m.equivalence] || EQUIV_BADGE.RELATED;
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{m.sourceControlName || `#${m.sourceControlId}`}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 500 }}>{m.targetControlName || `#${m.targetControlId}`}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, ...b }}>{m.equivalence}</span>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#6b7280' }}>{m.createdAt?.slice(0, 10)}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <button onClick={() => del(m.id)} title={t('MappingEditor.deleteMapping')} aria-label={t('MappingEditor.deleteMapping')} style={s.btn('#dc2626', true)}>
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
